<template>
  <form novalidate @submit.prevent="submitForm">
    <div class="telegram-form-column">
      <div class="telegram-form-heading">
        <h5>{{ title }}</h5>
        <small class="text-muted">{{ $t('telegramMessage.formDescription') }}</small>
      </div>

      <div class="form-group">
        <label class="font-weight-bold">{{ $t('telegramMessage.audience') }}</label>
        <a-radio-group :value="value.audience_type" class="audience-segment" @change="changeAudience">
          <a-radio-button value="shop">{{ $t('telegramMessage.shop') }} <span>{{ formModels.shops.length }}</span></a-radio-button>
          <a-radio-button value="driver">{{ $t('telegramMessage.driver') }} <span>{{ formModels.drivers.length }}</span></a-radio-button>
          <a-radio-button value="all">{{ $t('telegramMessage.all') }}</a-radio-button>
        </a-radio-group>
      </div>

      <div v-if="value.audience_type !== 'all'" class="form-group">
        <div class="tw-flex tw-justify-between tw-items-center">
          <label class="font-weight-bold mb-0">{{ $t('telegramMessage.targetRecipients') }}</label>
          <div class="telegram-target-actions">
            <button type="button" class="telegram-select-all" @click="selectAllRecipients">
              {{ selectAllLabel }}
            </button>
            <button v-if="selectedRecipientCount" type="button" class="telegram-clear-all" @click="clearAllRecipients">
              {{ $t('telegramMessage.clearAll') }}
            </button>
          </div>
        </div>
        <a-select
          :value="value.recipient_ids"
          class="tw-w-full tw-mt-2 telegram-recipient-select"
          mode="multiple"
          show-search
          option-filter-prop="children"
          :placeholder="$t('telegramMessage.chooseRecipients')"
          :filter-option="filterOption"
          :max-tag-count="3"
          @change="recipientIds => updateField('recipient_ids', recipientIds)"
        >
          <a-select-option v-for="recipient in availableRecipients" :key="recipient.value" :value="recipient.value">
            {{ recipient.label }}
          </a-select-option>
        </a-select>
        <small class="form-text text-muted">{{ $t('telegramMessage.targetRecipientsHint') }}</small>
      </div>
      <a-alert v-else class="tw-mb-4" type="info" show-icon :message="$t('telegramMessage.allRecipientsHint')" />

      <div class="form-group">
        <label class="font-weight-bold">{{ $t('telegramMessage.messageTitle') }} <span class="telegram-required">*</span></label>
        <input
          :value="value.title"
          class="form-control"
          :class="{ 'is-invalid': titleRequiredError }"
          required
          maxlength="120"
          :placeholder="$t('telegramMessage.titlePlaceholder')"
          @input="updateField('title', $event.target.value)"
        >
        <div v-if="titleRequiredError" class="invalid-feedback d-block">
          {{ $t('telegramMessage.requiredField') }}
        </div>
        <small class="form-text text-muted tw-text-right">
          {{ value.title.length }}/120 {{ $t('telegramMessage.characters') }}
        </small>
      </div>

      <div class="form-group">
        <label class="font-weight-bold">{{ $t('telegramMessage.messageBody') }} <span class="telegram-required">*</span></label>
        <textarea
          :value="value.content"
          class="form-control"
          :class="{ 'is-invalid': contentRequiredError || messageTooLong }"
          :maxlength="contentMaxLength"
          required
          rows="7"
          :placeholder="$t('telegramMessage.contentPlaceholder')"
          @input="updateField('content', $event.target.value)"
        />
        <small :class="['form-text tw-text-right', messageTooLong ? 'text-danger' : 'text-muted']">
          {{ combinedMessageLength }}/{{ telegramMessageLimit }} {{ $t('telegramMessage.characters') }}
          <span v-if="hasImage">({{ $t('telegramMessage.captionLimit') }})</span>
        </small>
        <div v-if="contentRequiredError" class="invalid-feedback d-block">
          {{ $t('telegramMessage.requiredField') }}
        </div>
        <div v-else-if="messageTooLong" class="invalid-feedback d-block">
          {{ $t('telegramMessage.messageTooLong') }}
        </div>
      </div>

      <div class="form-group">
        <label class="font-weight-bold">
          {{ $t('telegramMessage.attachedMedia') }}
          <small class="text-muted">({{ $t('telegramMessage.optional') }})</small>
        </label>
        <div class="telegram-upload-row">
          <div class="telegram-upload-icon"><i class="far fa-image" /></div>
          <div class="telegram-upload-copy">
            <strong>{{ uploadLabel }}</strong>
            <span>{{ uploadHint }}</span>
          </div>
          <label class="telegram-upload-action">
            {{ hasImage ? $t('telegramMessage.replaceImage') : $t('telegramMessage.chooseImage') }}
            <input ref="image" type="file" accept="image/jpeg,image/png,image/webp" @change="chooseImage">
          </label>
          <button v-if="hasImage" type="button" class="telegram-remove-image" @click="clearImage">
            <i class="fas fa-times" />
          </button>
        </div>
      </div>

      <div class="form-group">
        <label class="font-weight-bold">
          {{ $t('telegramMessage.scheduleSend') }}
          <small class="text-muted">({{ $t('telegramMessage.optional') }})</small>
        </label>
        <div class="telegram-schedule-row">
          <i class="far fa-calendar-alt" />
          <a-date-picker
            v-model="scheduledValue"
            show-time
            format="YYYY-MM-DD HH:mm"
            value-format="YYYY-MM-DD HH:mm"
            :placeholder="$t('telegramMessage.sendImmediately')"
          />
        </div>
        <small class="form-text text-muted">{{ $t('telegramMessage.scheduleHint') }}</small>
      </div>

      <div class="telegram-form-actions">
        <button type="button" class="telegram-action-button telegram-cancel-button" :disabled="loading" @click="$emit('cancel')">
          {{ $t('cancel') }}
        </button>
        <div class="telegram-primary-actions">
          <button type="button" class="telegram-action-button telegram-draft-button" :disabled="loading" @click="saveDraft">
            {{ $t('telegramMessage.saveDraft') }}
          </button>
          <a-button class="telegram-schedule-button" :loading="loading" :disabled="!scheduledAt || messageTooLong || loading" @click="scheduleForm">
            <i class="far fa-calendar-alt tw-mr-1" />{{ $t('telegramMessage.scheduleMessage') }}
          </a-button>
          <a-button type="primary" html-type="submit" :loading="loading" :disabled="messageTooLong || loading">
            <i class="fab fa-telegram-plane tw-mr-1" />{{ $t('telegramMessage.sendMessage') }}
          </a-button>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
export default {
  name: 'TelegramMessageForm',
  props: {
    value: { type: Object, required: true },
    formModels: { type: Object, required: true },
    loading: { type: Boolean, default: false },
    title: { type: String, required: true },
    scheduledAt: { type: String, default: '' },
    imagePreview: { type: String, default: null }
  },
  data: () => ({
    submitted: false,
    touched: {
      title: false,
      content: false
    }
  }),
  computed: {
    availableRecipients () {
      if (this.value.audience_type === 'driver') {
        return this.formModels.drivers.map(item => ({ value: item.driver_id, label: `${item.driver_code} - ${item.driver_name}` }))
      }
      return this.formModels.shops.map(item => ({ value: item.shop_id, label: `${item.shop_code} - ${item.shop_name}` }))
    },
    selectedRecipientCount () {
      return this.value.recipient_ids.length
    },
    selectAllLabel () {
      return this.value.audience_type === 'shop'
        ? this.$t('telegramMessage.selectAllShops')
        : this.$t('telegramMessage.selectAllDrivers')
    },
    uploadLabel () {
      if (this.value.image) return this.value.image.name
      return this.imagePreview ? this.$t('telegramMessage.currentDraftImage') : this.$t('telegramMessage.noImageAttached')
    },
    uploadHint () {
      if (this.value.image) return this.$t('telegramMessage.uploadReadyHint')
      return this.imagePreview ? this.$t('telegramMessage.keepExistingImageHint') : this.$t('telegramMessage.uploadImageHint')
    },
    hasImage () {
      return Boolean(this.value.image || (this.imagePreview && !this.value.remove_image))
    },
    telegramMessageLimit () {
      return this.hasImage ? 1024 : 4096
    },
    combinedMessageLength () {
      return `${this.value.title.trim()}

${this.value.content.trim()}`.length
    },
    contentMaxLength () {
      return Math.max(0, this.telegramMessageLimit - this.value.title.trim().length - 2)
    },
    messageTooLong () {
      return this.combinedMessageLength > this.telegramMessageLimit
    },
    titleRequiredError () {
      return (this.submitted || this.touched.title) && !this.value.title.trim()
    },
    contentRequiredError () {
      return (this.submitted || this.touched.content) && !this.value.content.trim()
    },
    canSubmit () {
      return this.value.title.trim() &&
        this.value.content.trim() &&
        !this.messageTooLong
    },
    canSchedule () {
      return this.canSubmit && Boolean(this.scheduledAt)
    },
    scheduledValue: {
      get () {
        return this.scheduledAt
      },
      set (value) {
        this.$emit('update:scheduledAt', value)
      }
    }
  },
  methods: {
    updateField (field, value) {
      if (Object.prototype.hasOwnProperty.call(this.touched, field)) {
        this.touched[field] = true
      }
      this.$emit('input', Object.assign({}, this.value, { [field]: value }))
    },
    validateRequiredFields () {
      this.submitted = true
      return Boolean(this.canSubmit)
    },
    saveDraft () {
      if (this.validateRequiredFields()) this.$emit('save-draft')
    },
    scheduleForm () {
      if (this.validateRequiredFields()) this.$emit('schedule')
    },
    submitForm () {
      if (this.validateRequiredFields()) this.$emit('submit')
    },
    changeAudience (event) {
      this.$emit('input', Object.assign({}, this.value, {
        audience_type: event.target.value,
        recipient_ids: []
      }))
    },
    selectAllRecipients () {
      this.updateField('recipient_ids', this.availableRecipients.map(recipient => recipient.value))
    },
    clearAllRecipients () {
      this.updateField('recipient_ids', [])
    },
    filterOption (input, option) {
      const children = option.componentOptions && option.componentOptions.children
      const label = children && children[0] ? children[0].text : ''
      return label.toLowerCase().indexOf(input.toLowerCase()) >= 0
    },
    chooseImage (event) {
      const file = event.target.files[0]
      if (!file) return
      this.$emit('input', Object.assign({}, this.value, { image: file, remove_image: false }))
      this.$emit('image-selected', file)
    },
    clearImage () {
      this.$emit('input', Object.assign({}, this.value, { image: null, remove_image: Boolean(this.imagePreview) }))
      if (this.$refs.image) this.$refs.image.value = ''
      this.$emit('image-cleared')
    }
  }
}
</script>

<style scoped>
.telegram-form-column { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 1px 2px rgba(15, 23, 42, .05); margin: 0; min-height: 0 !important; padding: 22px !important; }
.telegram-form-heading { border-bottom: 1px solid #f1f5f9; margin-bottom: 20px !important; padding-bottom: 15px; }
.telegram-form-heading h5 { color: #0f172a; font-size: 15px; font-weight: 700; letter-spacing: -.25px; line-height: 20px; margin-bottom: 4px; text-transform: none; }
.telegram-form-heading h5:before { color: #2563eb; content: "\f1d8"; font-family: "Font Awesome 5 Free"; font-size: 13px; margin-right: 7px; }
.telegram-form-heading small { color: #64748b; display: block; font-size: 11px; }
.telegram-form-column .form-group { margin-bottom: 16px; }
.telegram-form-column .font-weight-bold { color: #334155; display: block; font-size: 11px; letter-spacing: .45px; margin-bottom: 7px; text-transform: uppercase; }
.telegram-required { color: #dc2626; font-weight: 700; margin-left: 2px; }
.telegram-form-column .form-control { border-color: #e2e8f0; border-radius: 8px; min-height: 42px; }
.telegram-form-column .form-control:focus { border-color: #229ed9; box-shadow: 0 0 0 3px rgba(34, 158, 217, .12); }
.telegram-form-column textarea.form-control { min-height: 145px; padding: 14px; }
.telegram-form-column .form-group > small.form-text { color: #94a3b8; font-size: 11px; }
.audience-segment { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; display: flex; padding: 4px; width: 100%; }
.audience-segment .ant-radio-button-wrapper { background: #fff; border: 0; border-radius: 6px; color: #334155; flex: 1; font-weight: 600; height: 34px; line-height: 34px; text-align: center; }
.audience-segment .ant-radio-button-wrapper:first-child { border-radius: 8px 0 0 8px; }
.audience-segment .ant-radio-button-wrapper:last-child { border-radius: 0 8px 8px 0; }
.audience-segment .ant-radio-button-wrapper-checked { background: #2563eb; color: #fff; }
.audience-segment .ant-radio-button-wrapper span { background: #f1f5f9; border-radius: 999px; color: #475569; font-size: 10px; margin-left: 5px; padding: 2px 6px; }
.audience-segment .ant-radio-button-wrapper-checked span { background: rgba(255,255,255,.2); color: #fff; }
.audience-segment .ant-radio-button-wrapper:not(:first-child):before { display: none; }
.telegram-recipient-select >>> .ant-select-selection { border-color: #e2e8f0; border-radius: 8px; min-height: 44px; padding: 4px 7px; }
.telegram-recipient-select >>> .ant-select-selection__rendered { line-height: 30px; margin-left: 0; margin-right: 0; }
.telegram-recipient-select >>> .ant-select-search--inline { margin-left: 0; }
.telegram-recipient-select >>> .ant-select-search__field { min-width: 180px; }
.telegram-recipient-select >>> .ant-select-selection__choice { background: #eff6ff; border-color: #bfdbfe; border-radius: 6px; color: #1e40af; }
.telegram-target-actions { align-items: center; display: flex; gap: 8px; }
.telegram-select-all, .telegram-clear-all { align-items: center; background: #eff6ff; border: 1px solid #93c5fd; border-radius: 6px; color: #1d4ed8; display: inline-flex; font-size: 11px; font-weight: 600; line-height: 1.2; margin: 0 0 6px; padding: 6px 10px; transition: background-color .15s ease, border-color .15s ease, color .15s ease; }
.telegram-select-all:hover { background: #2563eb; border-color: #2563eb; color: #fff; }
.telegram-clear-all { background: #fff1f2; border-color: #fca5a5; color: #dc2626; }
.telegram-clear-all:hover { background: #dc2626; border-color: #dc2626; color: #fff; }
.telegram-select-all:active { background: #1d4ed8; border-color: #1d4ed8; color: #fff; }
.telegram-select-all:focus, .telegram-clear-all:focus { box-shadow: 0 0 0 3px rgba(37,99,235,.16); outline: 0; }
.telegram-upload-row { align-items: center; border: 1px solid #e2e8f0; border-radius: 7px; display: flex; min-height: 54px; padding: 7px 10px; }
.telegram-upload-icon { align-items: center; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 6px; color: #2563eb; display: flex; flex: 0 0 34px; height: 34px; justify-content: center; }
.telegram-upload-copy { display: flex; flex: 1; flex-direction: column; min-width: 0; padding: 0 10px; }
.telegram-upload-copy strong { font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.telegram-upload-copy span { color: #94a3b8; font-size: 10px; margin-top: 2px; }
.telegram-upload-action { border: 1px solid #dbe4ee; border-radius: 5px; color: #334155; cursor: pointer; font-size: 10px !important; letter-spacing: 0 !important; margin: 0 !important; padding: 6px 9px; text-transform: none !important; white-space: nowrap; }
.telegram-upload-action input { display: none; }
.telegram-remove-image { background: transparent; border: 0; color: #94a3b8; margin-left: 4px; padding: 3px; }
.telegram-schedule-row { align-items: center; border: 1px solid #e2e8f0; border-radius: 7px; display: flex; gap: 10px; min-height: 54px; padding: 7px 10px; }
.telegram-schedule-row > i { color: #2563eb; font-size: 15px; }
.telegram-schedule-row >>> .ant-calendar-picker { flex: 1; width: auto !important; }
.telegram-form-actions { align-items: center; border-top: 1px solid #f1f5f9; display: flex; gap: 12px; justify-content: space-between; margin-top: 3px; padding-top: 15px; }
.telegram-primary-actions { align-items: center; display: flex; gap: 7px; justify-content: flex-end; margin-left: auto; }
.telegram-form-actions .ant-btn { border-radius: 6px; font-size: 11px; padding: 7px 11px; }
.telegram-action-button { border-radius: 6px; font-size: 11px; padding: 7px 11px; transition: background-color .15s ease, border-color .15s ease, color .15s ease; }
.telegram-cancel-button { background: #fff; border: 1px solid #cbd5e1; color: #475569; }
.telegram-cancel-button:hover { background: #f8fafc; border-color: #94a3b8; color: #0f172a; }
.telegram-draft-button { background: #f8fafc; border: 1px solid #cbd5e1; color: #334155; }
.telegram-draft-button:hover { background: #e2e8f0; border-color: #94a3b8; color: #0f172a; }
.telegram-form-actions >>> .telegram-schedule-button { background: #d97706; border-color: #d97706; color: #fff; }
.telegram-form-actions >>> .telegram-schedule-button:hover,
.telegram-form-actions >>> .telegram-schedule-button:focus { background: #b45309; border-color: #b45309; color: #fff; }
.telegram-form-actions >>> .telegram-schedule-button[disabled] { background: #f3f4f6; border-color: #d1d5db; color: #6b7280; opacity: 1; }
@media (max-width: 575px) {
  .telegram-form-actions { align-items: stretch; flex-direction: column-reverse; }
  .telegram-primary-actions { flex-wrap: wrap; justify-content: flex-end; margin-left: 0; }
}
</style>
